/**
 * Sticky ATC bar (section__sticky-atc): scroll reveal, variant sync, PDP sync or cart drawer.
 */
(function () {
  "use strict";

  var globalSyncBound = false;

  function headerOffset() {
    var raw = getComputedStyle(document.documentElement).getPropertyValue("--header-height");
    var n = parseFloat(raw);
    return Number.isFinite(n) ? n : 0;
  }

  function formatMoney(cents) {
    if (window.theme && window.theme.utils && typeof window.theme.utils.formatMoney === "function") {
      return window.theme.utils.formatMoney(cents);
    }
    return "$" + (Number(cents) / 100).toFixed(2);
  }

  function parseProduct(jsonEl) {
    if (!jsonEl) return null;
    try {
      return JSON.parse(jsonEl.textContent || "{}");
    } catch (_e) {
      return null;
    }
  }

  function getProductFromOptions(productOptions) {
    if (!productOptions || !productOptions.dataset.product) return null;
    return parseProduct(document.getElementById(productOptions.dataset.product));
  }

  function getVariantFromProduct(product, variantId) {
    if (!product || !Array.isArray(product.variants) || !variantId) return null;
    return product.variants.find(function (v) {
      return String(v.id) === String(variantId);
    });
  }

  function stickyMatchesSource(root, productOptions, product) {
    if (!root || !productOptions) return false;

    var rootHandle = root.getAttribute("data-product-handle");
    var sourceHandle = productOptions.getAttribute("data-product-handle");
    if (rootHandle && sourceHandle) return rootHandle === sourceHandle;

    var rootId = root.getAttribute("data-product-id");
    return !!(product && rootId && String(product.id) === String(rootId));
  }

  function resolveVariantId(productOptions, input) {
    if (input && input.getAttribute("data-variant-id")) {
      return input.getAttribute("data-variant-id");
    }
    if (productOptions && productOptions.dataset.masterSelectId) {
      var select = document.getElementById(productOptions.dataset.masterSelectId);
      if (select && select.value) return select.value;
    }
    var sourceRoot = productOptions && productOptions.closest("s-product");
    if (sourceRoot) {
      var idField = sourceRoot.querySelector('select[name="id"], input[name="id"]');
      if (idField && idField.value) return idField.value;
    }
    return null;
  }

  function getVariantPriceMap(sourceRoot) {
    if (!sourceRoot) return {};
    var mapEl =
      sourceRoot.querySelector("[data-variant-price-map]") ||
      sourceRoot.querySelector("#ProductVariantPriceMap");
    if (!mapEl) return {};
    try {
      return JSON.parse(mapEl.textContent || "{}");
    } catch (_e) {
      return {};
    }
  }

  function getVariantFeaturedImage(variant, product) {
    if (!variant) return null;
    if (variant.featured_image && variant.featured_image.src) return variant.featured_image;

    if (variant.featured_media && product && Array.isArray(product.media)) {
      var media = product.media.find(function (m) {
        return String(m.id) === String(variant.featured_media.id);
      });
      if (media && media.preview_image && media.preview_image.src) return media.preview_image;
      if (media && media.src) return media;
    }

    if (product && product.featured_image && product.featured_image.src) return product.featured_image;
    return null;
  }

  function updateStickyPrice(root, variant, sourceRoot) {
    var priceDst = root.querySelector("[js-price]");
    if (!priceDst) return;

    var priceSrc = sourceRoot && sourceRoot.querySelector("[js-price]");
    if (priceSrc) {
      priceDst.innerHTML = priceSrc.innerHTML;
      return;
    }

    if (!variant) return;
    var priceMap = getVariantPriceMap(sourceRoot);
    var row = priceMap[String(variant.id)] || {};
    var price = typeof row.price === "number" ? row.price : variant.price;
    var compareAt =
      typeof row.compare_at_price === "number" ? row.compare_at_price : variant.compare_at_price;
    var onSale = compareAt > price;

    var regular = priceDst.querySelector(".price__regular");
    var compare = priceDst.querySelector(".price__compare");
    if (regular) {
      regular.textContent = formatMoney(price);
      regular.classList.toggle("inline", onSale);
      regular.classList.toggle("text-red", onSale);
    }
    if (compare) {
      if (onSale) {
        compare.textContent = formatMoney(compareAt);
        compare.style.display = "";
      } else {
        compare.style.display = "none";
      }
    }
  }

  function updateStickyImage(root, variant, sourceRoot, product) {
    var stickyImg = root.querySelector("[js-sticky-image]");
    if (!stickyImg || !variant) return;

    if (sourceRoot) {
      var tmpl = sourceRoot.querySelector('template[data-variant-sticky-image="' + variant.id + '"]');
      if (tmpl && tmpl.content && tmpl.content.firstElementChild) {
        stickyImg.innerHTML = tmpl.content.firstElementChild.outerHTML;
        return;
      }
    }

    var image = getVariantFeaturedImage(variant, product);
    if (!image || !image.src) return;

    var alt = image.alt ? String(image.alt).replace(/"/g, "&quot;") : "";
    stickyImg.innerHTML =
      '<picture class="w-full h-full"><img src="' +
      image.src +
      '" alt="' +
      alt +
      '" class="w-full h-full object-cover" loading="lazy"></picture>';
  }

  function updateStickyLabels(root, variant, sourceRoot) {
    var colorText = "";
    if (sourceRoot) {
      var colorSrc = sourceRoot.querySelector("[js-color-label]");
      if (colorSrc) colorText = colorSrc.textContent.trim();
    }
    if (!colorText && variant && variant.option1) colorText = variant.option1;

    root.querySelectorAll("[js-color-label-custom]").forEach(function (el) {
      if (colorText) el.textContent = colorText;
    });
  }

  function updateStickyButton(root, variant, sourceRoot) {
    var stickyBtn = root.querySelector("[js-sticky-atc]");
    if (!stickyBtn || !variant) return;

    var mainAtc = sourceRoot && sourceRoot.querySelector("[js-main-add-to-cart]");
    if (mainAtc) {
      if (mainAtc.disabled) stickyBtn.setAttribute("disabled", "");
      else stickyBtn.removeAttribute("disabled");
      var mainText = mainAtc.querySelector("[js-atc-text], [data-atc-text]");
      var stickyText = stickyBtn.querySelector("[js-atc-text], [data-atc-text]");
      if (mainText && stickyText) stickyText.textContent = mainText.textContent;
      return;
    }

    if (variant.available) stickyBtn.removeAttribute("disabled");
    else stickyBtn.setAttribute("disabled", "");

    var stickyText = stickyBtn.querySelector("[js-atc-text], [data-atc-text]");
    if (stickyText) {
      stickyText.textContent = variant.available ? "Add to Cart" : "Sold Out";
    }
  }

  function applyVariantToSticky(root, variant, sourceRoot, product) {
    if (!variant) return;

    var idInput = root.querySelector('form [name="id"]');
    if (idInput) idInput.value = String(variant.id);

    updateStickyLabels(root, variant, sourceRoot);
    updateStickyPrice(root, variant, sourceRoot);
    updateStickyImage(root, variant, sourceRoot, product);
    updateStickyButton(root, variant, sourceRoot);
  }

  function syncStickyFromSource(root, productOptions, input, eventVariant) {
    if (!stickyMatchesSource(root, productOptions, getProductFromOptions(productOptions))) return;

    var sourceRoot = productOptions.closest("s-product");
    var product = getProductFromOptions(productOptions);
    var variant =
      eventVariant ||
      getVariantFromProduct(product, resolveVariantId(productOptions, input)) ||
      null;

    if (!variant) return;
    applyVariantToSticky(root, variant, sourceRoot, product);
  }

  function syncAllStickiesFromOptions(productOptions, input, eventVariant) {
    if (!productOptions) return;
    document.querySelectorAll("[js-sticky-atc-root]").forEach(function (root) {
      syncStickyFromSource(root, productOptions, input, eventVariant);
    });
  }

  function bindGlobalVariantSync() {
    if (globalSyncBound) return;
    globalSyncBound = true;

    document.addEventListener(
      "change",
      function (event) {
        var input = event.target;
        if (!input || !input.matches("[js-product-option-selector]")) return;
        var productOptions = input.closest("product-options");
        if (!productOptions) return;

        window.setTimeout(function () {
          syncAllStickiesFromOptions(productOptions, input, null);
        }, 0);
      },
      true,
    );

    document.addEventListener("variant:change", function (event) {
      var variant = event.detail && event.detail.variant;
      var productOptions =
        (event.target && event.target.closest && event.target.closest("product-options")) ||
        null;

      if (!productOptions) {
        var sourceRoot = event.target && event.target.closest && event.target.closest("s-product");
        if (!sourceRoot) return;
        productOptions = sourceRoot.querySelector("product-options");
      }
      if (!productOptions) return;

      syncAllStickiesFromOptions(productOptions, null, variant);
    });
  }

  function findSourceRoot(root) {
    var syncSelector = root.getAttribute("data-sync-source");
    if (syncSelector) {
      var el = document.querySelector(syncSelector);
      if (el) return el.closest("s-product") || el;
    }
    var productId = root.getAttribute("data-product-id");
    if (!productId) return null;
    var nodes = document.querySelectorAll("s-product[data-product-id]");
    for (var i = 0; i < nodes.length; i++) {
      if (String(nodes[i].getAttribute("data-product-id")) === String(productId)) {
        return nodes[i];
      }
    }
    return null;
  }

  function getMainAtc(root) {
    var sourceRoot = findSourceRoot(root);
    return (
      (sourceRoot && sourceRoot.querySelector("[js-main-add-to-cart]")) ||
      document.querySelector("s-product [js-main-add-to-cart]")
    );
  }

  function getRevealAnchor(root) {
    var custom = root.getAttribute("data-scroll-anchor");
    if (custom) {
      var el = document.querySelector(custom);
      if (el) return el;
    }
    return getMainAtc(root);
  }

  function isInViewport(el, header) {
    if (!el) return false;
    var rect = el.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > header;
  }

  function shouldShowSticky(revealAnchor, mainAtc, hasCustomReveal) {
    if (!revealAnchor) return false;

    var header = headerOffset();
    var pastReveal = revealAnchor.getBoundingClientRect().top < header;

    if (!hasCustomReveal) {
      return pastReveal;
    }

    return pastReveal && !isInViewport(mainAtc, header);
  }

  function initRoot(root) {
    var sticky = root.querySelector("[js-sticky]");
    if (!sticky) return;

    var footer = document.querySelector("#shopify-section-footer");
    var behavior = root.getAttribute("data-behavior");
    var stickyBtn = root.querySelector("[js-sticky-atc]");
    var sourceRoot = findSourceRoot(root);
    var mainAtc = getMainAtc(root);
    var revealAnchor = getRevealAnchor(root);
    var hasCustomReveal = !!root.getAttribute("data-scroll-anchor");

    function onScroll() {
      if (!revealAnchor) {
        sticky.classList.remove("show");
        if (footer) footer.style.paddingBottom = "0px";
        return;
      }
      var show = shouldShowSticky(revealAnchor, mainAtc, hasCustomReveal);
      sticky.classList.toggle("show", show);
      if (footer) footer.style.paddingBottom = show ? sticky.offsetHeight + "px" : "0px";
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    if (sourceRoot) {
      var productOptions = sourceRoot.querySelector("product-options");
      if (productOptions) {
        syncAllStickiesFromOptions(productOptions, null, null);
      }
    }

    if (behavior === "sync_pdp" && stickyBtn && mainAtc) {
      stickyBtn.addEventListener("click", function (e) {
        e.preventDefault();
        if (stickyBtn.getAttribute("data-choose-size") === "true") {
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
        mainAtc.click();
      });
    }
  }

  function init() {
    bindGlobalVariantSync();
    document.querySelectorAll("[js-sticky-atc-root]").forEach(initRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  document.addEventListener("shopify:section:load", init);
})();
